import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import {
  HtmlEditor,
  Inject,
  Link,
  RichTextEditorComponent,
  Toolbar,
  Count,
  Resize,
  ToolbarType,
} from "@syncfusion/ej2-react-richtexteditor";
import { useSelector } from "react-redux";
import { darkModeSelector } from "@/redux/selectors/currentUser";

const items = [
  "Bold",
  "Italic",
  "Underline",
  "StrikeThrough",
  "SuperScript",
  "SubScript",
  "|",
  "FontName",
  "FontSize",
  "FontColor",
  "BackgroundColor",
  "|",
  "Alignments",
  "Blockquote",
  "|",
  "NumberFormatList",
  "BulletFormatList",
  "|",
  "Outdent",
  "Indent",
  "|",
  "CreateLink",
  "|",
  "FormatPainter",
  "ClearFormat",
  "|",
  "Undo",
  "Redo",
];

const RichTextEditor = forwardRef(
  (
    { value, onChange, placeholder, disabled = false, isDarkMode = undefined, height = "100%", width = "100%" },
    ref,
  ) => {
    const darkModeActivated = useSelector(darkModeSelector);
    const [isLayoutReady, setIsLayoutReady] = React.useState(false);
    const rteRef = React.useRef(null);

    React.useEffect(() => {
      setIsLayoutReady(true);
    }, []);

    // Determine if dark mode should be used
    const useDarkMode = isDarkMode !== undefined ? isDarkMode : darkModeActivated;

    // Handle dark mode styling through direct DOM manipulation
    React.useEffect(() => {
      if (rteRef.current && isLayoutReady) {
        const rteElement = rteRef.current.element;
        if (rteElement) {
          if (useDarkMode) {
            rteElement.classList.add("e-dark-mode");
          } else {
            rteElement.classList.remove("e-dark-mode");
          }
        }
      }
    }, [useDarkMode, isLayoutReady]);

    const handleChange = ({ value: content }) => {
      onChange(content);
    };

    // Combine refs
    const combinedRef = React.useCallback(
      element => {
        rteRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        }
      },
      [ref],
    );

    return (
      <div className="max-h-full">
        <RichTextEditorComponent
          ref={combinedRef}
          toolbarSettings={isLayoutReady ? { items, type: ToolbarType.Expand } : { items: [] }}
          placeholder={placeholder}
          value={value}
          autoSaveOnIdle
          saveInterval={50}
          change={handleChange}
          readonly={disabled}
          showCharCount
          width={width}
          enableResize
          height={height}
        >
          <Inject services={[Toolbar, HtmlEditor, Link, Count, Resize]} />
        </RichTextEditorComponent>
      </div>
    );
  },
);

RichTextEditor.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isDarkMode: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default RichTextEditor;
